import { useState } from 'react';
import { X, Building2, Upload } from 'lucide-react';

interface RecruiterOnboardingModalProps {
  isOpen: boolean;
  onComplete: (data: OnboardingData) => void;
  onSkip: () => void;
  needsEmailConfirmation?: boolean;
}

interface OnboardingData {
  organizationLogo?: File | null;
  companySize?: string;
  industry?: string;
}

export default function RecruiterOnboardingModal({
  isOpen,
  onComplete,
  onSkip,
  needsEmailConfirmation
}: RecruiterOnboardingModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState<OnboardingData>({});

  const totalSteps = 2;

  const updateProgress = (step: number) => {
    return (step / totalSteps) * 100;
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete(data);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFileUpload = (file: File | null) => {
    setData({ ...data, organizationLogo: file });
  };

  const selectCompanySize = (size: string) => {
    setData({ ...data, companySize: size });
  };

  const selectIndustry = (industry: string) => {
    setData({ ...data, industry });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 animate-fade-in">
      <div className="w-full max-w-xl mx-4 bg-white rounded-3xl shadow-xl overflow-hidden animate-scale-in">
        <div className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">
                Complete your organization profile
              </h3>
            </div>
            <button
              onClick={onSkip}
              className="text-slate-400 hover:text-slate-600 text-sm transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-full bg-blue-600 rounded-full transition-all duration-300"
                style={{ width: `${updateProgress(currentStep)}%` }}
              ></div>
            </div>
            <span className="text-xs text-slate-500">
              Step {currentStep} of {totalSteps}
            </span>
          </div>
        </div>

        <div className="px-6 py-6 min-h-[400px]">
          {currentStep === 1 && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <h4 className="text-base font-semibold text-slate-900 mb-1">
                  Tell us about your organization
                </h4>
                <p className="text-sm text-slate-500">
                  This helps candidates learn more about your company.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Company size
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: '1-10', label: '1-10 employees' },
                      { value: '11-50', label: '11-50 employees' },
                      { value: '51-200', label: '51-200 employees' },
                      { value: '201+', label: '201+ employees' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => selectCompanySize(option.value)}
                        className={`rounded-2xl border px-4 py-3 text-left text-sm transition ${
                          data.companySize === option.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-blue-500 hover:bg-blue-50/60'
                        }`}
                      >
                        <p className="font-medium text-slate-800">{option.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Industry
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: 'technology', label: 'Technology' },
                      { value: 'finance', label: 'Finance' },
                      { value: 'healthcare', label: 'Healthcare' },
                      { value: 'retail', label: 'Retail' },
                      { value: 'education', label: 'Education' },
                      { value: 'other', label: 'Other' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => selectIndustry(option.value)}
                        className={`rounded-2xl border px-4 py-3 text-left text-sm transition ${
                          data.industry === option.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-blue-500 hover:bg-blue-50/60'
                        }`}
                      >
                        <p className="font-medium text-slate-800">{option.label}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <h4 className="text-base font-semibold text-slate-900 mb-1">
                  Add your organization logo (optional)
                </h4>
                <p className="text-sm text-slate-500">
                  Your logo will appear on job postings and help candidates recognize your brand.
                </p>
                {needsEmailConfirmation && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                    Logo upload will be available after you confirm your email
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <label className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-slate-300 rounded-2xl py-12 cursor-pointer hover:border-blue-500 hover:bg-blue-50/40 transition">
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
                    <Upload className="w-8 h-8 text-slate-400" />
                  </div>
                  <div className="text-center">
                    <span className="text-sm font-medium text-slate-700 block mb-1">
                      Upload organization logo
                    </span>
                    <span className="text-xs text-slate-400">
                      PNG, JPG or SVG, up to 5MB
                    </span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e.target.files?.[0] || null)}
                    className="hidden"
                    disabled={needsEmailConfirmation}
                  />
                </label>
                {data.organizationLogo && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                    <div className="w-8 h-8 rounded bg-white flex items-center justify-center overflow-hidden">
                      <img
                        src={URL.createObjectURL(data.organizationLogo)}
                        alt="Logo preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">{data.organizationLogo.name}</p>
                      <p className="text-xs text-slate-500">
                        {(data.organizationLogo.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleFileUpload(null);
                      }}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 pb-5 pt-3 border-t border-slate-100 flex items-center justify-between">
          <button
            onClick={onSkip}
            className="text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2 transition"
          >
            Skip for now
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePrev}
              disabled={currentStep === 1}
              className="px-4 py-2 text-xs rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              Back
            </button>
            <button
              onClick={handleNext}
              className="px-5 py-2 text-xs rounded-full bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
            >
              {currentStep === totalSteps ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
